import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_URL } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export default function OAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { syncAuthSession } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [error, setError] = useState<string>('');
  const hasStartedExchange = useRef(false);

  const handleGoToWorkspace = () => {
    navigate('/crm/dashboard');
  };

  useEffect(() => {
    if (hasStartedExchange.current) return;
    hasStartedExchange.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setStatus('error');
      setError(`Google OAuth error: ${errorParam}`);
      return;
    }

    if (!code || !state) {
      setStatus('error');
      setError('Missing authorization code or state');
      return;
    }

    // Exchange code for tokens
    const exchangeCode = async () => {
      try {
        // Use auth endpoint for login/register
        const response = await fetch(`${API_URL}/api/auth/google`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code, state }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to complete OAuth');
        }

        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('connected_email', data.user.email);
        syncAuthSession({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          user: data.user,
        });

        setStatus('success');

        toast({
          title: 'Welcome!',
          description: `Signed in as ${data.user.email}`,
        });

        // If this is a popup, close it. Nothing listens for a message from
        // here, so we do not broadcast the session tokens to the opener.
        if (window.opener) {
          setTimeout(() => window.close(), 1500);
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        setError(err instanceof Error ? err.message : 'OAuth failed');
      }
    };

    exchangeCode();
  }, [searchParams, navigate, toast]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full p-8 text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
            <h1 className="text-xl font-semibold mb-2">Connecting Gmail...</h1>
            <p className="text-muted-foreground">
              Please wait while we complete the authorization.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <h1 className="text-xl font-semibold mb-2">Success!</h1>
            <p className="text-muted-foreground mb-4">
              Your Gmail account has been connected. You can now send emails directly from RaiseBeacon.
            </p>
            {!window.opener && (
              <Button onClick={handleGoToWorkspace}>
                Open workspace
              </Button>
            )}
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <h1 className="text-xl font-semibold mb-2">Connection Failed</h1>
            <p className="text-muted-foreground mb-4">{error}</p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={() => navigate(-1)}>
                Go Back
              </Button>
              <Button onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
